/**
 * «Запись на экране» для сигнала у строки и принятия итога (ТЗ главной § 8): над полосой видно не меньше
 * 60 % заголовка записи или — у раскрытой — не меньше 160 px записи. Наблюдается вся запись (`<li>`): у
 * длинной раскрытой (около 790 px на телефоне) ученик читает тело, когда заголовок уже за верхом окна, и
 * сигнал «Генерируем travel ↓» над записью, которую он читает, был бы ложным (§ 23.4 п. 2). Чистая функция
 * без alias-импортов — её собирают тесты.
 */

/** Над полосой видно меньше 60 % заголовка записи — её «нет на экране». */
export const VISIBLE_RATIO = 0.6;
/** Заголовок записи — `h-16`. */
export const HEADER_PX = 64;
/** Сколько раскрытой записи достаточно видеть. Столько же места под заголовком требует раскрытие (§ 22.5). */
export const BODY_VISIBLE_PX = 160;
/** Пороги наблюдателя через 5 %: у высокой раскрытой записи иначе не узнать, когда видно 160 px. */
export const VISIBILITY_THRESHOLDS = Array.from({ length: 21 }, (_, index) => index / 20);

export interface Band {
  top: number;
  bottom: number;
  height: number;
}

/**
 * Видимая часть записи — одна полоса (`intersectionRect`, уже за вычетом полосы создания в `rootMargin`);
 * сколько из неё приходится на заголовок, считаем по верху записи.
 */
export function isRecordVisible(entry: { isIntersecting: boolean; boundingClientRect: Band; intersectionRect: Band }): boolean {
  if (!entry.isIntersecting) return false;
  const box = entry.boundingClientRect;
  const seen = entry.intersectionRect;
  const headerHeight = Math.min(HEADER_PX, box.height);
  const headerSeen = Math.min(box.top + headerHeight, seen.bottom) - Math.max(box.top, seen.top);
  if (headerHeight > 0 && headerSeen >= headerHeight * VISIBLE_RATIO) return true;
  return seen.height >= BODY_VISIBLE_PX;
}

/** Общая часть двух полос по вертикали; разошлись — null (касание краями — полоса нулевой высоты). */
export function intersectBands(a: Band, b: Band): Band | null {
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.bottom, b.bottom);
  return bottom < top ? null : { top, bottom, height: bottom - top };
}

/**
 * То же решение по живой геометрии: прямоугольник записи, обрезка предками с `overflow` между ней и корнем
 * (`clips`) и видимая полоса корня над полосой создания (`band`). По ней решают все колбэки наблюдателей, а не
 * по их снимку: IntersectionObserver молчит, пока не сменилась доля пересечения, а у строки, которая въезжает по
 * высоте 0 → 64 целиком внутри корня, доля всё время 1 — её первое наблюдение при высоте 0 так и оставалось
 * «не на экране» (приёмка главной D, A2-01).
 */
export function isRecordVisibleIn(box: Band, clips: readonly Band[], band: Band): boolean {
  let seen = intersectBands(box, band);
  for (const clip of clips) seen = seen && intersectBands(seen, clip);
  if (!seen) return false;
  return isRecordVisible({ isIntersecting: true, boundingClientRect: box, intersectionRect: seen });
}
